import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type TaxConstructor } from '@webapp/platform/finance'
import {
  type CreateTaxPayload,
  type ITaxRepository,
  type TaxListFilterParams,
} from '@webapp/platform/finance/business-logic/interfaces/ITaxRepository'
import { mapTaxApiModel } from '../../mappers/mapTaxApiModel'
import { type GetTaxesResponse } from '../../types/GetTaxesResponse'
import { type GetTaxResponse } from '../../types/GetTaxResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class TaxRepository implements ITaxRepository {
  constructor(private readonly deps: Dependency) {}

  async getTaxes(
    params?: TaxListFilterParams
  ): Promise<RepoPaginatedResponse<TaxConstructor>> {
    // Default to active taxes so existing consumers (e.g. TaxSelect) are unaffected.
    const { isActive = true, ...filters } = params ?? {}

    const res = await this.deps.httpClient.get<GetTaxesResponse>(
      `/api/v3/tax/`,
      {
        params: {
          is_active: isActive,
          order_by: 'name',
          ...formatRepoFiltersToApiFilters(filters),
        },
      }
    )

    const taxes = res.data.data || []
    const metadata = res.data.metadata

    return {
      data: taxes.map(mapTaxApiModel),
      pagination: {
        count: metadata?.pagination.count || 0,
        page: metadata?.pagination.current_page || 0,
        pageSize: metadata?.pagination.page_size || 0,
        numPages: metadata?.pagination.num_pages || 0,
      },
    }
  }

  async getTax(id: number): Promise<RepoStandardResponse<TaxConstructor>> {
    const res = await this.deps.httpClient.get<GetTaxResponse>(
      `/api/v3/tax/${id}/`
    )

    const data = res.data.data

    return {
      data: mapTaxApiModel(data),
    }
  }

  async createTax(payload: CreateTaxPayload): Promise<TaxConstructor> {
    try {
      const res = await this.deps.httpClient.post<GetTaxResponse>(
        `/api/v3/tax/`,
        {
          id: null,
          name: payload.name,
          percentage: payload.percentage,
          is_inclusive: payload.isInclusive,
          description: payload.description,
        },
        {
          params: { format: 'json' },
        }
      )

      return mapTaxApiModel(res.data.data)
    } catch (error) {
      return throwRepoErrorFromApiV3Error(error)
    }
  }

  async updateTax(payload: TaxConstructor): Promise<TaxConstructor> {
    try {
      const res = await this.deps.httpClient.put<GetTaxResponse>(
        `/api/v3/tax/${payload.id}/`,
        {
          id: payload.id,
          name: payload.name,
          percentage: payload.percentage,
          description: payload.description,
          is_active: payload.isActive,
          is_default: payload.isDefault,
          is_inclusive: payload.isInclusive,
        }
      )

      return mapTaxApiModel(res.data.data)
    } catch (error) {
      return throwRepoErrorFromApiV3Error(error)
    }
  }

  async setDefaultTax(id: number): Promise<TaxConstructor> {
    try {
      const res = await this.deps.httpClient.patch<GetTaxResponse>(
        `/api/v3/tax/${id}/`,
        {
          is_default: true,
          is_active: true,
        }
      )

      return mapTaxApiModel(res.data.data)
    } catch (error) {
      return throwRepoErrorFromApiV3Error(error)
    }
  }

  async deactivateTax(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/tax/${id}/`, {
        params: { format: 'json' },
      })
    } catch (error) {
      throwRepoErrorFromApiV3Error(error)
    }
  }
}
